import React, { useState, useEffect, useCallback } from 'react';
import ReactFlow, { Node, Edge, Controls, MarkerType, Handle, Position, ConnectionMode, useNodesState, useEdgesState } from 'reactflow';
import 'reactflow/dist/style.css';
import { Box, CircularProgress, Chip, Alert } from '@mui/material';
import { getReviewEntity } from '../../services/api';
import { CONFIG } from '../../config/config';
import { getToken } from '../../services/auth';

interface EntityHierarchyTabProps {
  parentEntityId: number;
  onEntitySelect: (entityId: number) => void;
  onEntityNameFound?: (name: string) => void;
}

interface NodeData { 
  id: number; 
  name: string; 
  entityType: string; 
  x: number; 
  y: number; 
  isPrimary: boolean; 
  childOfId?: number; // For edges: this node is child of childOfId
}

const EntityNode: React.FC<any> = ({ data }) => {
  const handleClick = () => { if (data.onEntitySelect) data.onEntitySelect(data.entityId); };
  return (
    <div 
      onClick={handleClick} 
      style={{ 
        padding: '10px', 
        borderRadius: '8px', 
        border: data.isPrimary ? '3px solid #1976d2' : '2px solid #90caf9', 
        background: data.isPrimary ? '#e3f2fd' : '#fff', 
        width: '160px', 
        textAlign: 'center', 
        cursor: 'pointer', 
        boxShadow: data.isPrimary ? '0 4px 12px rgba(25, 118, 210, 0.4)' : '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'all 0.2s ease',
      }}
    >
      <Handle type="source" position={Position.Bottom} id="source-bottom" style={{ background: '#1976d2' }} />
      <Handle type="target" position={Position.Top} id="target-top" style={{ background: '#1976d2' }} />
      <div style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '0.85rem' }}>{data.label}</div>
      <Chip label={data.entityType} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
      {data.isPrimary && <Chip label="Current" size="small" color="primary" sx={{ fontSize: '0.65rem', ml: 0.5 }} />}
    </div>
  );
};

const nodeTypes = { entityNode: EntityNode };

const EntityHierarchyTab: React.FC<EntityHierarchyTabProps> = ({ parentEntityId, onEntitySelect, onEntityNameFound }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const processedIds = new Set<number>();
    const nodeDataList: NodeData[] = [];

    // Collect parents recursively (going UP the hierarchy)
    const collectParents = async (id: number, level: number, x: number, y: number, childOfId?: number) => {
      if (level > 5 || processedIds.has(id)) return;
      processedIds.add(id);
      try {
        const res = await getReviewEntity(id);
        const entity = res.data;
        
        // Report entity name for the primary entity
        if (id === parentEntityId && onEntityNameFound) {
          onEntityNameFound(entity.name);
        }
        
        nodeDataList.push({ 
          id, 
          name: entity.name, 
          entityType: entity.review_entity_type?.display_label || 'Unknown', 
          x, y, 
          isPrimary: id === parentEntityId, 
          childOfId 
        });
        
        // Traverse parents (going UP)
        if (entity.parents?.length) {
          const nodeWidth = 180;
          const spacing = nodeWidth + 40; // node width + gap
          const totalWidth = (entity.parents.length - 1) * spacing;
          const startX = x - totalWidth / 2;
          for (let i = 0; i < entity.parents.length; i++) {
            const p = entity.parents[i];
            if (p?.id && !processedIds.has(p.id)) {
              await collectParents(p.id, level + 1, startX + i * spacing, y - 140, id);
            }
          }
        }
      } catch (e) { console.error(e); }
    };

    // Fetch children for the selected entity
    const fetchChildren = async (entityId: number, x: number, y: number) => {
      try {
        const token = getToken();
        const response = await fetch(
          `${CONFIG.SERVER_URL}/dashboard/entity/children?entity_id=${entityId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        if (response.ok) {
          const children = await response.json();
          if (children && children.length > 0) {
            // Use wider spacing for children to prevent overlap
            const nodeWidth = 180;
            const spacing = nodeWidth + 40; // node width + gap
            const totalWidth = (children.length - 1) * spacing;
            const startX = x - totalWidth / 2;
            for (let i = 0; i < children.length; i++) {
              const child = children[i];
              if (!processedIds.has(child.id)) {
                processedIds.add(child.id);
                nodeDataList.push({
                  id: child.id,
                  name: child.name,
                  entityType: child.entity_type_display_label || 'Unknown',
                  x: startX + i * spacing,
                  y: y + 140, // Increased vertical spacing
                  isPrimary: false,
                  childOfId: entityId
                });
              }
            }
          }
        }
      } catch (e) { console.error('Error fetching children:', e); }
    };

    const build = async () => {
      setLoading(true);
      setError('');
      processedIds.clear();
      nodeDataList.length = 0;
      
      // First collect parents (going UP from selected entity)
      await collectParents(parentEntityId, 0, 0, 200);
      
      // Then fetch children (going DOWN from selected entity)
      await fetchChildren(parentEntityId, 0, 200);
      
      // Build ReactFlow nodes
      const flowNodes: Node[] = nodeDataList.map(d => ({ 
        id: `e-${d.id}`, 
        type: 'entityNode', 
        position: { x: d.x, y: d.y }, 
        data: { label: d.name, entityType: d.entityType, isPrimary: d.isPrimary, entityId: d.id, onEntitySelect } 
      }));
      
      // Build edges: connect each node to its parent (childOfId)
      const flowEdges: Edge[] = nodeDataList
        .filter(d => d.childOfId)
        .map(d => ({ 
          id: `edge-${d.id}-${d.childOfId}`, 
          source: `e-${d.childOfId}`,  // Parent is source
          target: `e-${d.id}`,          // Child is target
          sourceHandle: 'source-bottom', 
          targetHandle: 'target-top', 
          type: 'straight', 
          style: { stroke: '#2196f3', strokeWidth: 2 }, 
          markerEnd: { type: MarkerType.ArrowClosed, color: '#2196f3' } 
        }));
      
      setNodes(flowNodes);
      setEdges(flowEdges);
      setLoading(false);
    };
    
    build().catch(e => { setError(String(e)); setLoading(false); });
  }, [parentEntityId, onEntitySelect, onEntityNameFound]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ height: 'calc(100vh - 280px)', minHeight: 400, width: '100%' }}>
      <ReactFlow 
        nodes={nodes} 
        edges={edges} 
        nodeTypes={nodeTypes} 
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        fitView 
        fitViewOptions={{ padding: 0.3 }} 
        connectionMode={ConnectionMode.Loose} 
        nodesConnectable={false} 
        nodesDraggable={true} 
        zoomOnScroll={true} 
        panOnScroll={true}
        minZoom={0.2} 
        maxZoom={2}
      >
        <Controls />
      </ReactFlow>
    </Box>
  );
};

export default EntityHierarchyTab;
